import React, { useEffect, useState } from "react";
import { Button, FormGroup, Input, Label } from "reactstrap";
import { createOwnershipClaim, getOwnershipClaim } from "../../services/collectorApi";

type Claim = {
  status?: string;
  rejectReason?: string;
  closedReason?: string;
};

const ERRORS: Record<string, string> = {
  PIECE_NOT_SOLD: "This piece hasn't been billed to a buyer yet, so there is nothing to claim. If you bought it recently, try again once the studio has billed it.",
  CLAIM_PENDING: "Your claim is already with the studio.",
  CLAIM_LIMIT_REACHED: "The studio has turned down this claim several times. Please contact the studio directly.",
  ALREADY_OWNED: "This piece is already in your collection as owned.",
};

const CLOSED_TEXT: Record<string, string> = {
  SALE_CHANGED: "The sale was returned or changed, so your earlier claim was closed.",
  ANOTHER_CLAIM_APPROVED: "The studio confirmed another buyer for this piece.",
  MERGED: "Your earlier claim was closed when you merged accounts.",
};

/**
 * On a scanned HKP piece: "I bought this". Used when the studio billed the sale to a different
 * or missing phone number, so the piece didn't join the buyer's collection automatically.
 */
const OwnershipClaimCard: React.FC<{ itemId: string; onOwned: () => void }> = ({ itemId, onOwned }) => {
  const [claim, setClaim] = useState<Claim | null>(null);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getOwnershipClaim(itemId)
      .then((res) => !cancelled && setClaim(res.data?.data || { status: "NONE" }))
      .catch(() => !cancelled && setClaim({ status: "NONE" }));
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const submit = async () => {
    setSending(true);
    setError("");
    try {
      const res = await createOwnershipClaim(itemId, {
        note: note.trim() || undefined,
        invoiceNumber: invoiceNumber.trim() || undefined,
      });
      const data = res.data?.data || {};
      if (data.status === "AUTO_MATCHED") {
        onOwned();
        return;
      }
      setClaim(data);
      setOpen(false);
    } catch (e: any) {
      const msg = e?.response?.data?.message || "";
      setError(ERRORS[msg] || msg || "Could not send your claim");
    } finally {
      setSending(false);
    }
  };

  if (!claim) return null;
  const status = claim.status || "NONE";
  if (status === "APPROVED") return null;
  const canClaim = status !== "PENDING";

  return (
    <div className="mc-card mb-3">
      <h2 className="h6 mb-1">Did you buy this piece?</h2>
      {status === "PENDING" && (
        <p className="small text-muted mb-0">Your claim is with the studio. Once they approve it, the piece moves to My pieces as yours.</p>
      )}
      {status === "REJECTED" && (
        <p className="small mb-2">
          The studio didn't approve your claim{claim.rejectReason ? `: “${claim.rejectReason}”` : "."}
        </p>
      )}
      {status === "CLOSED" && (
        <p className="small text-muted mb-2">{CLOSED_TEXT[claim.closedReason || ""] || "Your earlier claim was closed."}</p>
      )}
      {status === "NONE" && !open && (
        <p className="small text-muted mb-2">
          Purchases join your collection on their own when the studio bills them to your number.
          If it was billed to another number, or without one, ask the studio to confirm it's yours.
        </p>
      )}
      {canClaim && !open && (
        <Button className="mc-btn-secondary" onClick={() => { setError(""); setOpen(true); }}>
          {status === "NONE" ? "Claim this piece" : "Claim again"}
        </Button>
      )}
      {open && (
        <div>
          <FormGroup>
            <Label for="claimInvoice">Invoice number (optional)</Label>
            <Input
              id="claimInvoice"
              value={invoiceNumber}
              maxLength={60}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="From your bill, if you have it"
            />
          </FormGroup>
          <FormGroup>
            <Label for="claimNote">Note for the studio (optional)</Label>
            <Input
              id="claimNote"
              type="textarea"
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Where and when you bought it, the name or number on the bill"
            />
          </FormGroup>
          <div className="mc-form-actions">
            <Button className="mc-btn-primary" onClick={submit} disabled={sending}>
              {sending ? "Sending…" : "Send to studio"}
            </Button>
            <Button className="mc-btn-ghost" onClick={() => setOpen(false)} disabled={sending}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {error && <p className="mc-field-error mt-2 mb-0">{error}</p>}
    </div>
  );
};

export default OwnershipClaimCard;
